// Haftalık Rapor — sahibin elle tuttuğu "VS" tablosunun panel karşılığı.
// ─────────────────────────────────────────────────────────────────────────────
// Veri SHEET taramasından gelir (GET /api/weekly-report), bu app'in Postgres'inden
// DEĞİL: çağrı sayısı ile yatırım tutarı AYNI taramadan çıkar, böylece birbirini
// tutmak zorundadır. Soğuk bir hafta ~10 sn sürer.
//
// Sayfa dört kuralı taşır:
//   §1  Veri erişimi yalnız api.ts (getWeeklyReport) — burada fetch yok.
//   §2  TR kaynak, EN kaplama: her durağan dizge i18n.ts sözlüğünde; her dinamik
//       dizge TEK JSX ifadesi (sayı taşıyanlar translatePhrase regex'iyle).
//       Sheet değerleri (liste/kaynak/sebep/temsilci adları) KASITLI çevrilmez.
//   §3  Stiller inline, renkler App.tsx'in CSS değişkenleri + onaylı semantik hex'ler.
//   §6  Eksik değere yer tutucu YOK: null bir para/pay hücresi BOŞ kalır (0 DEĞİL).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, getStatusSystems, getWeeklyReport } from '../api';
import { brandLabel, smsTemplateLabel, WEEKLY_REPORT_BRANDS } from '../constants';
import { addDays, computePreset, downloadCSV, fmtDate, fmtHMS, fmtMoney, fmtPct1, fmtStamp, weekStartOf } from '../format';
import { useNow } from '../hooks';
import { downloadPNG } from '../png-export';
import { rankAgents, type RankedAgent } from '../agent-ranking';
import type {
  BrandId,
  Lang,
  StatusKey,
  WeeklyAgentQms,
  WeeklyAgentSms,
  WeeklyAgentTalk,
  WeeklyBrandReport,
  WeeklyReportData,
  WeeklySmsSummary,
  WeeklyTableRowKind,
} from '../types';
import { isWeeklyBrandError } from '../types';
import {
  cardStyle,
  CsvButton,
  dateInputStyle,
  dotStyle,
  EmptyState,
  Icon,
  PngButton,
  SectionTitle,
  segStyle,
  Skeleton,
} from '../ui';

// ── Sabitler ─────────────────────────────────────────────────────────────────

/** Hafta daima İstanbul takvimindedir (WEEKLY_REPORT_BRANDS = Vegas + Prime + Hera, üçü
 *  de İstanbul dilimi) — tarayıcının yerel saati KULLANILMAZ. */
const TZ = 'Europe/Istanbul';

/** Sunucunun kabul ettiği en eski hafta (MAX_WEEKS_BACK). Her SOĞUK hafta 2 workbook
 *  taraması + 7 BackOffice çağrısı demek; kutuyu geriye doğru sonsuz kaydırmak
 *  paylaşılan servis hesabının kotasını yakardı. */
const MAX_WEEKS_BACK = 26;

/** 429 `busy` yanıtında `Retry-After` okunamazsa (dev CORS başlığı gizleyebilir). */
const BUSY_FALLBACK_SEC = 30;

/** "08.09.2026 – 14.09.2026" — hafta başlığı ve PNG şeridi aynı biçimi basar. */
const weekRangeLabel = (week: string) => fmtDate(week) + ' – ' + fmtDate(addDays(week, 6));

const WARNING_TR: Record<string, string> = {
  historicalFormats: 'Bu haftanın bazı satırlarında eski tarih biçimi var; sayılar biraz sapabilir.',
  mutableSource: 'Kaynak sekmeler hâlâ düzenlenebilir; hafta kapanana kadar sayılar değişebilir.',
};

// Sonuç sütunlarının etiketi VE rengi tek kaynaktan gelir — bu Türkçe dizgeler
// asla elle yazılmaz (i18n karşılıkları zaten sözlükte).
// 2026-09-15 (sahip): pre-2026-09-09 miras statüleri (Yanıtladı/Ulaşılamadı/Meşgul/Memnun Değil)
// bu rapordan KALDIRILDI. Motor stray miras satırı modern kanona KATLIYOR (weekly-report.js
// LEGACY_CANON_FOLD) → wire'a hiç düşmezler, sütunları da burada eleniyor. 'other' (Diğer) KALIR:
// öğrenilmemiş proxy statüsü gerçek bir kova. Miras StatusDef'ler getStatusSystems().tr'de DURUR
// (canlı Raporlar sayfası + BetBooker EN hâlâ kullanıyor) — yalnız BU tablo süzer.
const LEGACY_STATUS_KEYS = new Set<StatusKey>(['answered', 'unreachable', 'busy', 'dissatisfied']);
const RESULTS = getStatusSystems().tr.filter((st) => !LEGACY_STATUS_KEYS.has(st.key));

// "Yanıtlayan" = sahibin listelediği ALTI sonuç (Task Board #260/#261, 2026-10-16): Olumlu,
// Olumsuz / İlgilenmiyor, Meşgul / Tekrar Aranacak, Aranmak İstemiyor, Konuşma Esnasında Kapattı,
// Telefonu Başkası Açtı (+ onlara katlanan legacy answered/busy/dissatisfied). Geri kalan her sonuç
// NA — Markayı Duyunca Kapattı dahil. Kanon anahtar kümesi sunucununkiyle
// (src/engine/status-map.js ANSWERED_CANONS) BİREBİR aynı olMALI (test/status-map.test.mjs pinler);
// etiket yerine anahtarla süzülür ki bir yazım değişikliği sessizce kaymasın. Arama Yapılmadı =
// no_call: motorun `total`'ına girer ama gerçek bir arama DEĞİL — Toplam'dan düşülür (pic 4).
const ANSWERED_KEYS = new Set<StatusKey>([
  'positive', 'not_interested', 'call_back', 'do_not_call', 'hung_up_mid_call', 'answered_by_other',
  'answered', 'busy', 'dissatisfied',
]);
const NO_CALL_LABEL = RESULTS.find((st) => st.key === 'no_call')?.label ?? 'Arama Yapılmadı';

// ── Donut paleti ─────────────────────────────────────────────────────────────
// dataviz: kategorik renkler SABİT SIRAYLA atanır, asla döngüye alınmaz ve 8.
// slottan sonra yeni renk ÜRETİLMEZ — kuyruk tek nötr "Diğer" kovasına iner.
// Sıra scripts/validate_palette.js ile seçildi (dark, surface var(--surface)):
//   CVD ayrımı ΔE 12.9 (protan) · normal görüş ΔE 20.8 — ikisi de PASS.
// Kırmızı (#ef5d5d) KASITLI dışarıda: bu panelde durum rengidir (hata şeridi,
// aşım, kritik uyarı) ve bir kategoriye verilirse anlamı bulanır; ayrıca pembeyle
// (#e0639b) normal görüşte ΔE 8.8 — tek başına ayırt edilemeyecek kadar yakın.
const DONUT_COLORS = ['#3d8bff', '#37c46b', '#a97cf0', '#f2784b', '#2fb8a8', '#e0a23c', '#e0639b'];
// Nötr gri: katlanmış kuyruk ("Diğer") VE renk paletini bir taşıran tek kategori için.
// Adı yazılı kaldığı sürece nötr renk bilgi kaybettirmez; katlamak kaybettirirdi.
const OTHER_COLOR = 'var(--dim)';
const OTHER_LABEL = 'Diğer';

// Donut geometrisi (viewBox 180×180).
const CX = 90;
const CY = 90;
const R_OUT = 78;
const R_IN = 50;

// ── Tablo ızgarası ───────────────────────────────────────────────────────────
// Sıra: LİSTE · TOPLAM · <sonuç sütunları> · CEVAPLAMA · 12S · 36S · GÖRÜŞÜLEN ÜYE.
// Sonuç sütunu SAYISI RESULTS'tan TÜRETİLİR (elle 6 yazılmaz): durum kanonları
// değişince (2026-09-09'da 6→13) sabit ızgara başlıkları/hücreleri örtük satıra
// sarıp tabloyu bozuyordu — izler artık RESULTS.length'e bağlı. Her sonuç sütunu
// 58px; başlık 10px'te birden çok satıra sarar (en uzun tek kelime sığacak taban).
//
// YATIRIM PAYI 24S/48S sütunları 98px: liste satırları burada % (%20,9) taşır AMA
// Toplam satırı tam TL payda tutarını basar (1.040.599,72 / 2.339.932,59) — sağa
// yaslı sayı taşarsa komşuya biner (grid hücresi kırpmaz), o yüzden en geniş içerik
// olan Toplam satırının TL değeri sığacak genişlik verilir. LİSTE 1fr'ye çekildi
// (ellipsis zaten var) ki geniş ekranda sayı sütunlarından yer çalmasın.
const RESULT_COL_PX = 58;
// BAŞARI 24S / 48S (görev #202/#203/#204, 2026-10-10) CEVAPLAMA ile YATIRIM PAYI 24S ARASINDA: yatıran ÷ görüşme,
// karnenin BAŞARI ORANI'yla aynı tanım. %'li kısa değer → CEVAPLAMA genişliği (64px).
const TABLE_COLS = ['minmax(104px,1fr)', '56px']
  .concat(RESULTS.map(() => RESULT_COL_PX + 'px'))
  .concat(['64px', '64px', '64px', '98px', '98px', '70px'])
  .join(' ');
// Izgara satırının kenarlığı/zemini tam genişlikte olsun diye (yoksa yatay scroll'da
// kesilir) min-width tüm sabit izleri + gap'leri + yatay padding'i kapsar:
// LİSTE 104 + TOPLAM 56 + N sonuç×58 + CEVAPLAMA 64 + BAŞARI 64×2 + 12S/36S 98×2 + ÜYE 70
// + 8px×(iz sayısı−1) gap + 36px yatay padding. İz sayısı = N + 8.
const TABLE_MIN_WIDTH =
  104 + 56 + RESULTS.length * RESULT_COL_PX + 64 + 64 + 64 + 98 + 98 + 70 + (RESULTS.length + 7) * 8 + 36 + 'px';

// TEMSİLCİ · TOPLAM ÇAĞRI · YANITLAYAN · YATIRIM YAPAN · BAŞARI · TUTAR (huni sırası:
// çağrı → yanıtlayan → yatıran). Toplam Çağrı en soldaki metrik. Ad sütununa minmax TABANI:
// altı sütun tam-genişlik kartta sığar, ama 1fr ASLA sıfıra çökmesin (adlar kaybolmasın).
// Yarım-karta sığacak sıkı düzen: ad sütunu esner, sabitler ~596px'e sığar → §AgentTables
// auto-fit eşiği (600px) bu tabloyu kırpmadan iki sütuna geçebilir. Geniş ekranda 1fr açar.
// QMS KALİTE + KRİTİK sit to the RIGHT of the money column (owner, 2026-09-06): the
// deposit funnel stays intact and the quality read is appended, not interleaved.
// + SKOR (görev #143, Asya 2026-09-29: "there is no overall score") — Haftanın Temsilcisi skoru
// karnenin kendisinde; satırlar bu skora göre yukarıdan aşağı.
// Yalnız Vegas + Prime (Asya 2026-09-29: "this is only for vegas and prime, remove this update from hera").
// + SMS (görev #151/#152, 2026-09-29) TOPLAM ÇAĞRI'nın hemen sağında — ulaşma çabası yan yana;
// değer Postgres sms_events'ten (haftalık, iki pencerede aynı — TOPLAM ÇAĞRI gibi).
// + KONUŞMA SÜRESİ (2026-10, softphone call_sessions → agent_day_summary) SMS'in sağında — çağrı
// çabasının üçüncü ölçüsü; sunucuda API katmanında birleşir (cache DIŞI), oturumsuz satır BOŞ.
// QMS KALİTE + KRİTİK HATA yalnız Vegas + Prime (görev #207, Asya 2026-10-09): qms-panel başka
// marka bilmez → Hera'da iki sütun hep '—' idi; ızgara, CSV ve toplam satırından düşer.
const AGENT_COLS = 'minmax(88px,1fr) 74px 56px 70px 80px 90px 84px 108px';
const QMS_COLS = ' 72px 62px';
const SCORED_BRANDS: readonly BrandId[] = ['vegas', 'prime'];
const QMS_BRANDS: readonly BrandId[] = ['vegas', 'prime'];
// "WhatsApp - Telegram" kartı + "WhatsApp / Telegram" halkası yalnız Vegas + Prime (görev #255/#256, Asya
// 2026-10-14: "remove WhatsApp - Telegram … in hera"). Sunucu Hera'nın `WP TG` sekmesini hâlâ okur (wire
// değişmedi, dış API'de `messaging` kalır) — yalnız panel çizmez. QMS_BRANDS gibi SPA-only.
const MESSAGING_BRANDS: readonly BrandId[] = ['vegas', 'prime'];

// SIRA · TEMSİLCİ · QMS PUANI · BAŞARI PUANI · YATIRIM PUANI · KRİTİK CEZA · SKOR — Haftanın Temsilcisi kartı.
// Sabit izler + gap + padding ~672px: AgentTables'taki kartın 680px auto-fill eşiğine sığar.
const RANK_COLS = '52px minmax(88px,1fr) 92px 100px 88px 100px 72px';

// ── Adaptörler ───────────────────────────────────────────────────────────────
// Sunucu yarısı bu sayfayla eş zamanlı yazılıyor. Ham şekle DOĞRUDAN bakan tek yer
// bu iki fonksiyondur: bir anahtar geç gelirse ya da adı değişirse sayfa çizmeye
// devam eder, yalnız o kart "henüz gelmiyor" der.

/** Mesaj sekmesinin sebep dağılımı (WhatsApp / Telegram). */
function extractMessagingReasons(b: WeeklyBrandReport): Record<string, number> | null {
  const m = b.messaging;
  return m && m.byReason ? m.byReason : null;
}

/** Bir arama-talebi sekmesinin (SLACK/FORM/TAKİP) sebep dağılımı — col H. Sunucu
 *  `callRequests`'i henüz göndermiyorsa null (kart "henüz gelmiyor" der); kova varsa ama
 *  boşsa {} (kart "Veri yok" der). İkisi AYRI durumdur. */
function extractCallRequestReasons(b: WeeklyBrandReport, source: string): Record<string, number> | null {
  const cr = b.callRequests;
  if (!Array.isArray(cr)) return null;
  const bucket = cr.find((c) => c && c.source === source);
  return bucket ? bucket.byReason || {} : {};
}

/** BONUS sekmesinin C-sütunu kampanya kırılımı (RETENTION / CHURN / FREESPIN …) → çağrı
 *  sayısı. Sunucu `bonusLists`'i henüz göndermiyorsa null; gönderiyor ama boşsa {} —
 *  aynı iki durum. Etiket sheet'in birebir metni: sözlüğe GİRMEZ (§2). */
function extractBonusLists(b: WeeklyBrandReport): Record<string, number> | null {
  const bl = b.bonusLists;
  if (!Array.isArray(bl)) return null;
  const m: Record<string, number> = {};
  bl.forEach((x) => { if (x && x.list) m[x.list] = (m[x.list] || 0) + (x.calls || 0); });
  return m;
}

/**
 * Tablo satırları: her sekme bir satır. Çağrısı 0 olan listeler (ör. Prime TAKİP)
 * en alta iner; her grup içinde sekme sırası korunur (kararlı bölme).
 */
function tableRows(b: WeeklyBrandReport): WeeklyTableRowKind[] {
  const lists = Array.isArray(b.lists) ? b.lists : [];
  const rows: WeeklyTableRowKind[] = lists
    .filter((l) => !!l)
    .map((l) => ({
      key: 'list:' + l.list,
      label: l.list,
      subLabel: '',
      calls: l.calls,
      conversations: l.conversations,
      answerRatePct: l.answerRatePct,
      results: l.results,
      depositShare12hPct: l.depositShare12hPct,
      depositShare36hPct: l.depositShare36hPct,
      deposited12h: l.deposited12h ?? null,
      deposited36h: l.deposited36h ?? null,
      successRate12hPct: l.successRate12hPct ?? null,
      successRate36hPct: l.successRate36hPct ?? null,
    }));
  const withCalls = rows.filter((r) => (r.calls || 0) > 0);
  const zeroCalls = rows.filter((r) => !(r.calls || 0));
  return withCalls.concat(zeroCalls);
}

/** Sonuç kırılımını (Yanıtladı/Meşgul/…) TÜM sekmeler üzerinden toplar → ListsTable'ın
 *  `totals`'ı ile birebir aynı sayı; toplamı = summary.calls. */
function resultTotals(report: WeeklyBrandReport): Record<string, number> {
  const acc: Record<string, number> = {};
  tableRows(report).forEach((r) =>
    RESULTS.forEach((st) => (acc[st.label] = (acc[st.label] || 0) + ((r.results || {})[st.label] || 0))),
  );
  return acc;
}

/** Yanıtlayan sayısı = `ANSWERED_KEYS` (hatta bir insan olan her sonuç) — 2026-09-09 ÖNCESİNİN
 *  "Yanıtladı + Meşgul + Memnun Değil"i DEĞİL. Görüşme sayısı bundan DAR: `Meşgul / Tekrar
 *  Aranacak` ve `Markayı Duyunca Kapattı` yanıtlanandır ama görüşme değildir (sunucu tarafı,
 *  `CONVERSATION_CANONS`), o yüzden bu sayı `conversations`'tan büyük olur. */
function answeredOf(totals: Record<string, number>): number {
  return RESULTS.filter((st) => ANSWERED_KEYS.has(st.key)).reduce((n, st) => n + (totals[st.label] || 0), 0);
}

/** Bir sonuç kırılımının GÖSTERİLEN sütun toplamı — motorun `calls`'ı DEĞİL: tabloda
 *  sütunu OLMAYAN statüleri (not_found/BULUNAMADI, boş-canon, unknownResults) saymaz. Hem
 *  Toplam satırı hem her liste satırı bu tabana oturur → satır TOPLAM'ları Toplam satırına
 *  birebir toplanır. */
function shownResultTotal(totals: Record<string, number>): number {
  return RESULTS.reduce((n, st) => n + (totals[st.label] || 0), 0);
}

/** "Gerçek arama" toplamı = gösterilen sonuçlar − Arama Yapılmadı. no_call motorun
 *  `total`'ına girer ama gerçek bir arama değildir (pic 4) — Toplam satırından DA her liste
 *  satırından da düşülür ki iki taban aynı kalsın. no_call zaten gösterilen sütunlardan
 *  biri olduğu için sonuç asla negatif olamaz. */
function callsExclNoCallOf(totals: Record<string, number>): number {
  return shownResultTotal(totals) - (totals[NO_CALL_LABEL] || 0);
}

/**
 * answered ÷ (gösterilen sonuçlar − Arama Yapılmadı), tek ondalık.
 *
 * **Hiç arama yoksa `null`** (2026-09-23, sahibin uyarısı): boş bir sekmede `%0,0`, "aradık,
 * kimse açmadı" demektir — oysa o hafta o listeden HİÇ arama çıkmamıştır. Ölçüm yokluğu bir
 * ölçüm değildir; §6'nın "eksik değere yer tutucu YOK" kuralı ve Temsilci Detayı'nın
 * `rateCell`'i (görüşme yoksa BOŞ) ile aynı karar. `fmtPct1(null)` boş dizge basar.
 *
 * Toplam satırı bundan ETKİLENMEZ ve zaten etkilenmiyordu: Toplam, satır oranlarının
 * ORTALAMASI değil, sütunları toplayıp bir kez bölen HAVUZLANMIŞ orandır — 0 çağrılı bir
 * liste hem paya hem paydaya 0 katar. CANLI DOĞRULAMA (6 marka-hafta, 2026-09-23): boş
 * listeler dahil ve hariç HER İKİ toplam da BİREBİR aynı çıktı — vegas 14.09 1287÷7868
 * = %16,4 (boş: EKSTRA, DAVET), prime 1970÷11024, hera 2690÷11696. Satır ortalaması
 * olsaydı vegas %22,2 derdi. Bu yüzden "boş sekmeyi Toplam'dan çıkar" diye bir filtre
 * YOK ve olmamalı: çıkarılacak bir katkı yok, filtre yalnızca yanlış bir model kurar.
 */
function answerRateExclNoCall(totals: Record<string, number>): number | null {
  const base = callsExclNoCallOf(totals);
  return base > 0 ? Math.round((1000 * answeredOf(totals)) / base) / 10 : null;
}

/** MoneyStrip'in "Toplam Çağrı"sı (Arama Yapılmadı HARİÇ) — Canlı Durum'un "Bu hafta" şeridi de
 *  bunu okur (görev #195/#196), iki ekran aynı haftada aynı sayıyı göstersin diye. */
export function weeklyRealCalls(report: WeeklyBrandReport): number {
  return callsExclNoCallOf(resultTotals(report));
}

/**
 * Liste Kırılımı Toplam satırının BAŞARI'sı: Σ yatıran ÷ aynı satırın GÖRÜŞÜLEN ÜYE'si (havuzlanmış, satır ortalaması
 * DEĞİL — cevaplama oranıyla aynı model; okuyucu aritmetiği o satırdan doğrulayabilir). DAVET'in görüşmeleri paydada
 * kalır: karnedeki her temsilcinin BAŞARI'sı da onları sayar (sahibin 2026-08-12 kararı). Hiçbir satır yatıran sayısı
 * taşımıyorsa (eski sunucu) ya da görüşme yoksa null → BOŞ.
 */
export function pooledListSuccessPct(
  rows: { deposited12h: number | null; deposited36h: number | null }[],
  conversations: number,
  window12: boolean,
): number | null {
  const counts = rows.map((r) => (window12 ? r.deposited12h : r.deposited36h));
  if (!(conversations > 0) || !counts.some((n) => n !== null)) return null;
  const n = counts.reduce<number>((acc, v) => acc + (v || 0), 0);
  return Math.round((1000 * n) / conversations) / 10;
}

/** BAŞARI hücresinin title'ı ("12 yatıran / 48 görüşme"); sayı yoksa title yok. EN: i18n translatePhrase regex'i. */
function successTitle(deposited: number | null, conversations: number): string | undefined {
  return deposited === null || !(conversations > 0) ? undefined : fmtMoney(deposited) + ' yatıran / ' + fmtMoney(conversations) + ' görüşme';
}

/** Karnenin BAŞARI ORANI paydası = görüşme − DAVET (sahip, görev #297; DAVET'in yatırım penceresi yok). Eski sunucu
 *  `successBase` göndermez → görüşme (o sunucunun oranı da görüşme üzerindendi). */
export const agentSuccessBase = (r: { conversations: number; successBase?: number }): number =>
  typeof r.successBase === 'number' ? r.successBase : r.conversations;

/** Karne BAŞARI ORANI hücresinin title'ı; payda YANITLAYAN ÜYE'den küçükse "(DAVET hariç)" der. */
function agentSuccessTitle(deposited: number, r: { conversations: number; successBase?: number }): string | undefined {
  const base = agentSuccessBase(r);
  if (!(base > 0)) return undefined;
  return fmtMoney(deposited) + ' yatıran / ' + fmtMoney(base) + ' görüşme' + (base < r.conversations ? ' (DAVET hariç)' : '');
}

// ── Donut geometrisi ─────────────────────────────────────────────────────────

interface Slice {
  key: string;
  label: string;
  value: number;
  pct: number;
  color: string;
}

/** 0° saat 12 yönü, saat yönünde artar. */
function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

const n2 = (v: number) => v.toFixed(2);

/** Halka dilimi: dış yay → iç yarıçapa in → iç yayı GERİ çiz → kapat. */
function donutPath(a0: number, a1: number): string {
  const large = a1 - a0 > 180 ? 1 : 0;
  const [x0o, y0o] = polar(CX, CY, R_OUT, a0);
  const [x1o, y1o] = polar(CX, CY, R_OUT, a1);
  const [x1i, y1i] = polar(CX, CY, R_IN, a1);
  const [x0i, y0i] = polar(CX, CY, R_IN, a0);
  return (
    'M' + n2(x0o) + ',' + n2(y0o) +
    ' A' + R_OUT + ',' + R_OUT + ' 0 ' + large + ' 1 ' + n2(x1o) + ',' + n2(y1o) +
    ' L' + n2(x1i) + ',' + n2(y1i) +
    ' A' + R_IN + ',' + R_IN + ' 0 ' + large + ' 0 ' + n2(x0i) + ',' + n2(y0i) +
    ' Z'
  );
}

/**
 * Bir sayaç haritasını dilimlere çevirir. Sıfır/negatif/NaN değerler düşer, büyükten
 * küçüğe sıralanır, kuyruk tek nötr "Diğer" kovasına iner (dataviz: 9. kategoriye renk
 * ÜRETİLMEZ). Sıralı olduğu için bir kez katlanmaya başlandığında kuyruğun tamamı katlanır.
 *
 * Katlama kuralı RENK SAYISIDIR, pay değil. Bir pay eşiği burada yanlış olurdu: Vegas'ta
 * SLACK/FORM/TAKİP toplam çağrının %1,9/%0,8/%0,6'sı — hepsi %2'nin altında, yani sayfanın
 * VAR OLMA SEBEBİ olan üç liste tek bir "Diğer" diliminde kaybolurdu (sahibin kendi
 * pastası da %1,4'lük dilimleri ayrı gösteriyor).
 *
 * Tek bir kategori asla katlanmaz: onu "Diğer" diye çizmek aynı dilimi adını silerek
 * göstermek olurdu — bilgiyi azaltır, hiçbir şey kazandırmaz.
 */
function buildSlices(map: Record<string, number> | null | undefined): { slices: Slice[]; total: number } {
  const entries = Object.keys(map || {})
    .map((k) => [k, Number((map as Record<string, number>)[k])] as [string, number])
    .filter(([, v]) => Number.isFinite(v) && v > 0)
    .sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  if (!total) return { slices: [], total: 0 };
  // Tam sığıyorsa (veya sadece bir tanesi taşıyorsa) hiç katlama yok.
  const limit = entries.length <= DONUT_COLORS.length + 1 ? entries.length : DONUT_COLORS.length;
  const keep: [string, number][] = [];
  let rest = 0;
  entries.forEach(([k, v]) => {
    if (keep.length < limit) keep.push([k, v]);
    else rest += v;
  });
  const slices: Slice[] = keep.map(([k, v], i) => ({
    key: k,
    label: k,
    value: v,
    pct: (v / total) * 100,
    color: DONUT_COLORS[i] || OTHER_COLOR,
  }));
  if (rest > 0) {
    slices.push({ key: '__diger__', label: OTHER_LABEL, value: rest, pct: (rest / total) * 100, color: OTHER_COLOR });
  }
  return { slices, total };
}

// ── PNG dışa aktarma ─────────────────────────────────────────────────────────

/**
 * Kartı ekran görüntüsü olarak indiren tıklama işleyicisi + meşgul bayrağı. Sahibin isteği
 * (2026-09-16): Liste Kırılımı geniş ekranda bile yatay kaydırır, tek ekran görüntüsüne sığmaz →
 * PNG kırpılan sütunlar dahil TÜM tabloyu basar (png-export.ts). Hata yalnız konsola: canvas
 * çizimi ağa çıkmaz, gerçekçi tek arıza canvas boyut sınırı.
 */
function usePngExport(cardRef: React.RefObject<HTMLDivElement | null>, filename: string): [boolean, () => void] {
  const [busy, setBusy] = useState(false);
  const run = useCallback(() => {
    const el = cardRef.current;
    if (!el || busy) return;
    setBusy(true);
    downloadPNG(el, filename)
      .catch((e) => console.error('[png-export]', e))
      .finally(() => setBusy(false));
  }, [cardRef, filename, busy]);
  return [busy, run];
}

/** Yalnız PNG'de görünen şerit (png-export.ts `data-png-only`): kart tek başına paylaşılınca
 *  hangi marka/hafta olduğu okunsun. "Haftalık Rapor" AYRI düğüm → EN kaplaması çevirir; marka
 *  + tarih format değeri, çevrilmez. Ekranda display:none — dışa aktarma açar, geri kapatır. */
function PngCaption({ brand, week }: { brand: BrandId; week: string }) {
  return (
    <div
      data-png-only
      style={{ display: 'none', padding: '13px 18px', borderBottom: '1px solid var(--border)', fontSize: '12.5px', color: 'var(--dim)' }}
    >
      <span style={{ fontWeight: 700, color: 'var(--text)' }}>Haftalık Rapor</span>
      {' · ' + brandLabel(brand) + ' · ' + weekRangeLabel(week)}
    </div>
  );
}

// ── Küçük görsel parçalar ────────────────────────────────────────────────────

const num: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };

const stripStyle = (color: string, bg: string, border: string): React.CSSProperties => ({
  marginBottom: '14px',
  padding: '10px 13px',
  borderRadius: '10px',
  background: bg,
  border: '1px solid ' + border,
  color,
  fontSize: '13px',
});

const pillStyle = (color: string, bg: string, border: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  padding: '5px 11px',
  borderRadius: '999px',
  fontSize: '11.5px',
  fontWeight: 600,
  color,
  background: bg,
  border: '1px solid ' + border,
  whiteSpace: 'nowrap',
});

const iconBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  color: 'var(--dim)',
  cursor: 'pointer',
};

// Başlık hücreleri BÜYÜK HARF, ama sonuç sütunlarının etiketi elle yazılmaz —
// getStatusSystems().tr'den gelir ve DOM'da sözlükteki TR anahtarın AYNISI kalması
// gerekir (yoksa EN kaplaması eşleşemez). Bu yüzden büyük harf CSS'e bırakılır.
//
// TUZAK: `text-transform` DİLE DUYARLIDIR. index.html <html lang="tr"> taşır, yani
// Türkçe modda 'Memnun Değil' → 'MEMNUN DEĞİL' (noktalı İ) DOĞRU çıkar; ama EN
// kaplaması metni 'Dissatisfied' yaptığında aynı Türkçe kural 'DİSSATİSFİED'
// üretirdi. Bu yüzden başlık satırına o anki arayüz dili `lang` olarak yazılır —
// iki yön de doğru büyür.
const headCellStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '.04em',
  color: 'var(--faint)',
  textTransform: 'uppercase',
};

const cardHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 16px',
  borderBottom: '1px solid var(--border)',
};

/** Etiket + sayı AYRI düğümlerde: sayı hiçbir sözlük kalıbına takılmaz, etiket
 *  tek başına çevrilir (§2 bölünmüş düğüm deseni). Değer boşsa satır hiç basılmaz. */
function LabelNum({ label, value, color }: { label: string; value: string; color?: string }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontSize: '11.5px', color: 'var(--faint)' }}>
      <span>{label}</span>
      <span style={{ color: color || 'var(--dim)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

/** Para/oran kutucuğu. `note` = değer yerine geçen TR açıklama (BackOffice yoksa) —
 *  sayı UYDURULMAZ, kutu yine de neden boş olduğunu söyler. */
function StatTile({
  title,
  value,
  note,
  newRow,
  children,
}: {
  title: string;
  value?: string;
  note?: string;
  /** Izgarada YENİ satırın ilk hücresi — önceki satır eksik kartla bittiyse (Hera'da mesaj kartı yok) satırlar kaymasın. */
  newRow?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        ...cardStyle,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        ...(newRow ? { gridColumnStart: 1 } : null),
      }}
    >
      <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' }}>{title}</div>
      {value ? (
        <div style={{ fontSize: '21px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
          {value}
        </div>
      ) : null}
      {note ? <div style={{ fontSize: '12px', color: 'var(--dim)' }}>{note}</div> : null}
      {children}
    </div>
  );
}

/**
 * Halka grafik + yan efsane. Kütüphane YOK — path yayları elle kurulur.
 * Tek dilim 360°'lik bir yay olur ve yay komutu noktaya çöker; o durumda çizgili
 * bir <circle>'a düşülür. Son dilim tam 360°'ye kıstırılır (float artığı kapanmaz).
 * Ayırıcı: yüzey rengiyle 1.5px iz — dilim etrafına KENARLIK çizilmez.
 * Etiket/kılavuz çizgisi SVG'nin İÇİNE konmaz; kimlik efsanededir (renk + metin).
 */
function DonutCard({
  title,
  sub,
  map,
}: {
  title: string;
  sub: string;
  map: Record<string, number> | null;
}) {
  const { slices, total } = useMemo(() => buildSlices(map), [map]);
  const arcs = useMemo(() => {
    if (slices.length < 2) return [];
    let acc = 0;
    return slices.map((s, i) => {
      const a0 = acc;
      const a1 = i === slices.length - 1 ? 360 : acc + (s.value / total) * 360;
      acc = a1;
      return { key: s.key, color: s.color, d: donutPath(a0, a1) };
    });
  }, [slices, total]);

  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <div style={{ ...cardHeadStyle, borderBottom: '1px solid var(--border)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{title}</div>
          <div style={{ fontSize: '11px', color: 'var(--faint)' }}>{sub}</div>
        </div>
      </div>
      {map === null ? (
        <div style={{ padding: '34px 18px', textAlign: 'center', color: 'var(--faint)', fontSize: '12.5px' }}>
          Bu kırılım henüz sunucudan gelmiyor.
        </div>
      ) : total === 0 ? (
        <div style={{ padding: '34px 18px', textAlign: 'center', color: 'var(--faint)', fontSize: '12.5px' }}>Veri yok</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', flexWrap: 'wrap' }}>
          <svg viewBox="0 0 180 180" style={{ width: '150px', height: '150px', flex: 'none', display: 'block' }}>
            {slices.length === 1 ? (
              <circle
                cx={CX}
                cy={CY}
                r={(R_OUT + R_IN) / 2}
                fill="none"
                stroke={slices[0].color}
                strokeWidth={R_OUT - R_IN}
              />
            ) : (
              arcs.map((a) => <path key={a.key} d={a.d} fill={a.color} style={{ stroke: 'var(--surface)' }} strokeWidth="1.5" />)
            )}
          </svg>
          <div style={{ flex: 1, minWidth: '150px', maxHeight: '220px', overflowY: 'auto' }}>
            {/* Sütun etiketleri: yüzde ve adet kolonlarını adlandırır. Nokta + etiket
                bölgesi boş bir esnek boşlukla köprülenir; iki tag alttaki sayı
                kolonlarıyla AYNI genişlik + sağa yaslamayla hizalanır (§2: TR literal,
                EN kaplaması i18n.ts'te). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 0 6px', minWidth: 0 }}>
              <span style={{ width: '9px', flex: 'none' }} />
              <span style={{ flex: 1, minWidth: 0 }} />
              <span style={{ ...headCellStyle, minWidth: '48px', textAlign: 'right' }}>Yüzde</span>
              <span style={{ ...headCellStyle, minWidth: '38px', textAlign: 'right' }}>Adet</span>
            </div>
            {slices.map((s) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', minWidth: 0 }}>
                <span style={dotStyle(s.color, 9)} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '12px',
                    color: 'var(--text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.label}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', minWidth: '48px', textAlign: 'right' }}>
                  {fmtPct1(s.pct)}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums', minWidth: '38px', textAlign: 'right' }}>
                  {fmtMoney(s.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sayfa ────────────────────────────────────────────────────────────────────

export default function WeeklyReport({ brands, lang }: { brands: BrandId[]; lang: Lang }) {
  const thisWeek = useMemo(() => computePreset('this_week', TZ).start, []);
  const minWeek = useMemo(() => addDays(thisWeek, -7 * MAX_WEEKS_BACK), [thisWeek]);

  // Açılış haftası GEÇEN hafta, bu hafta değil: "haftalık rapor" bitmiş haftayı anlatır
  // (endpoint'in kendi varsayılanı da `last`). Çarşamba açan biri bu haftayı görseydi, 36
  // saatlik yatırım pencereleri henüz kapanmadığı için yarım bir tabloyu tam sanırdı.
  const [week, setWeek] = useState<string>(() => addDays(thisWeek, -7));
  const [data, setData] = useState<WeeklyReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  /** 429 `busy` — bu ana kadar bekle, sonra BİR kez kendiliğinden yeniden dene. */
  const [busyUntil, setBusyUntil] = useState<number | null>(null);

  const seq = useRef(0);
  const autoRetried = useRef(false);

  const scope = useMemo(() => brands.filter((b) => WEEKLY_REPORT_BRANDS.includes(b)), [brands]);
  const scopeKey = scope.join(',');

  const load = useCallback(async () => {
    if (!scopeKey) {
      setData(null);
      setLoading(false);
      return;
    }
    const my = ++seq.current;
    setLoading(true);
    setErr(null);
    setBusyUntil(null);
    // http() 5xx/ağ hatasında FIRLATIR — yakalanmazsa meşgul bayrağı asılı kalır ve
    // sayfa sonsuza dek iskelette durur (Breaks.tsx'in aynı dersi).
    try {
      const d = await getWeeklyReport(week, scope);
      if (my !== seq.current) return; // eskiyen istek — son seçim kazanır
      setData(d);
      autoRetried.current = false;
    } catch (e) {
      if (my !== seq.current) return;
      if (e instanceof ApiError && e.status === 429) {
        const sec = e.retryAfter && e.retryAfter > 0 ? Math.min(e.retryAfter, 300) : BUSY_FALLBACK_SEC;
        setBusyUntil(Date.now() + sec * 1000);
      } else {
        setErr(e instanceof Error && e.message ? e.message : 'request_failed');
      }
    } finally {
      if (my === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, scopeKey]);

  useEffect(() => {
    void load();
  }, [load]);

  // Hafta/kapsam değişince otomatik yeniden deneme hakkı tazelenir.
  useEffect(() => {
    autoRetried.current = false;
  }, [week, scopeKey]);

  // Kesinleşme anı gelince BİR kez yeniden yükle. `windowClosed` yalnız render'da hesaplanır;
  // açık bırakılan sayfa yoksa "Geçici veri" pill'ini yenile'ye basılana dek taşırdı. +30 sn:
  // sunucunun saati de anı geçmiş olsun (sunucu o an önce-hesaplanmış satırı yeniden tarar).
  useEffect(() => {
    if (!data || data.final) return;
    const ms = Date.parse(data.finalAfter) - Date.now();
    if (!(ms > 0) || ms > 2 ** 31 - 1 - 30_000) return;
    const id = window.setTimeout(() => void load(), ms + 30_000);
    return () => window.clearTimeout(id);
  }, [data, load]);

  // Geri sayım YALNIZ meşgul şeridi açıkken tıklar (boşta zamanlayıcı kurulmaz).
  const now = useNow(1000, busyUntil !== null);
  const busySec = busyUntil === null ? 0 : Math.max(0, Math.ceil((busyUntil - now) / 1000));

  useEffect(() => {
    if (busyUntil === null || now < busyUntil) return;
    setBusyUntil(null);
    // TEK deneme: ikinci 429 kullanıcıya bırakılır (yenile butonu). Sonsuz bir
    // yeniden deneme döngüsü, sunucudaki eşzamanlı SOĞUK hesap sınırını besler.
    if (autoRetried.current) {
      setErr('busy');
      return;
    }
    autoRetried.current = true;
    void load();
  }, [busyUntil, now, load]);

  const goWeek = (next: string) => setWeek(next);
  const onPreset = (p: 'this_week' | 'last_week') => setWeek(computePreset(p, TZ).start);
  const onDate = (v: string) => {
    if (v) setWeek(weekStartOf(v));
  };

  const canPrev = week > minWeek;
  const canNext = week < thisWeek;
  const weekLabel = weekRangeLabel(week);

  // Gelen veri İSTENEN haftaya ait olana kadar iskelet durur: soğuk hesap ~10 sn
  // sürüyor ve bu pencerede geçen haftanın parası YENİ haftanın başlığı altında
  // görünürdü.
  const stale = !data || data.week.start !== week;
  const showSkeleton = scope.length > 0 && (loading || stale) && !err && busyUntil === null;
  const showBody = scope.length > 0 && !stale && !!data;

  // Geçmiş (kesinleşme saati GEÇMİŞ) bir hafta için "Geçici veri" bildirimini HİÇ gösterme.
  // `final` = zaman ∧ sıfır bekleyen hücre; kesinleşme anı geçtiği hâlde yazılmamış birkaç
  // yatırım hücresi haftayı sonsuza dek `final:false` tutabilir (depositcheckup donmuş/ölü
  // ya da korumalı sekme — 03.08 haftasında yaşandı). Kapanmış bir hafta için amber pill +
  // "kaynak sekmeler düzenlenebilir" uyarısı bilgi vermez, yalnız kafa karıştırır. Karşılaştırma
  // epoch-ms (finalAfter UTC ISO) → tarayıcı saat diliminden bağımsız. Cari hafta (saat henüz
  // gelmemiş) pill'i taşımaya DEVAM eder; yeşil "Kesinleşti" ROZETİ de dokunulmaz.
  const windowClosed = !!data && Date.parse(data.finalAfter) <= Date.now();
  const showProvisional = !!data && !data.final && !windowClosed;
  const statusWarnings = (data?.warnings || []).filter((w) => !(windowClosed && w === 'mutableSource'));
  const showStatusRow =
    !!data && !stale && (data.final || showProvisional || statusWarnings.some((w) => WARNING_TR[w]));

  return (
    <>
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.06em', color: 'var(--faint)', marginRight: '2px' }}>
            HAFTA
          </span>
          <button onClick={() => onPreset('this_week')} style={segStyle(week === thisWeek)}>
            Bu Hafta
          </button>
          <button onClick={() => onPreset('last_week')} style={segStyle(week === addDays(thisWeek, -7))}>
            Geçen Hafta
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '6px' }}>
            <button
              onClick={() => canPrev && goWeek(addDays(week, -7))}
              title="Önceki hafta"
              disabled={!canPrev}
              style={{ ...iconBtnStyle, opacity: canPrev ? 1 : 0.4, cursor: canPrev ? 'pointer' : 'default' }}
            >
              <Icon name="chevron_left" size={18} />
            </button>
            <input type="date" value={week} onChange={(e) => onDate(e.target.value)} style={dateInputStyle} />
            <button
              onClick={() => canNext && goWeek(addDays(week, 7))}
              title="Sonraki hafta"
              disabled={!canNext}
              style={{ ...iconBtnStyle, opacity: canNext ? 1 : 0.4, cursor: canNext ? 'pointer' : 'default' }}
            >
              <Icon name="chevron_right" size={18} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
            <Icon name="event" size={17} color="var(--faint)" />
            <span style={{ fontSize: '12.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{weekLabel}</span>
            <button onClick={() => void load()} title="Yenile" style={iconBtnStyle}>
              <Icon name="refresh" size={18} />
            </button>
          </div>
        </div>

        {showStatusRow && data && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {data.final ? (
              <span style={pillStyle('#37c46b', 'rgba(55,196,107,.12)', 'rgba(55,196,107,.32)')}>
                <span style={dotStyle('#37c46b')} />
                Kesinleşti
              </span>
            ) : showProvisional ? (
              <span style={pillStyle('#e0a23c', 'rgba(224,162,60,.12)', 'rgba(224,162,60,.32)')}>
                <span style={dotStyle('#e0a23c')} />
                Geçici veri · kesinleşme:
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtStamp(data.finalAfter, TZ)}</span>
              </span>
            ) : null}
            {statusWarnings
              .map((w) => WARNING_TR[w])
              .filter(Boolean)
              .map((text) => (
                <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: 'var(--faint)' }}>
                  <Icon name="info" size={15} color="#e0a23c" />
                  {text}
                </span>
              ))}
          </div>
        )}
      </div>

      {err && <div style={stripStyle('#ef5d5d', 'rgba(239,93,93,.10)', 'rgba(239,93,93,.35)')}>{'Haftalık rapor yüklenemedi — ' + err}</div>}

      {busyUntil !== null && (
        <div style={stripStyle('#e0a23c', 'rgba(224,162,60,.10)', 'rgba(224,162,60,.35)')}>
          {'Rapor hesaplanıyor — ' + busySec + ' sn sonra tekrar denenecek'}
        </div>
      )}

      {scope.length === 0 && (
        <EmptyState
          icon="calendar_view_week"
          title="Haftalık rapor yok"
          reason="Haftalık rapor yalnızca Vegas ve Prime için üretilir; üstteki marka filtresinden birini seçin."
        />
      )}

      {showSkeleton && <Skeleton heights={[96, 190, 300]} />}

      {showBody &&
        data &&
        WEEKLY_REPORT_BRANDS.filter((b) => scope.includes(b)).map((b) => (
          <BrandSection key={b} brand={b} slot={data.brands[b]} week={week} lang={lang} />
        ))}
    </>
  );
}

// ── Marka bölümü ─────────────────────────────────────────────────────────────

function BrandSection({
  brand,
  slot,
  week,
  lang,
}: {
  brand: BrandId;
  slot: WeeklyReportData['brands'][BrandId];
  week: string;
  lang: Lang;
}) {
  const name = brandLabel(brand);

  const head = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 2px 14px' }}>
      <span
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '9px',
          background: 'var(--accent-soft)',
          border: '1px solid var(--accent-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <Icon name="storefront" size={17} color="var(--accent)" />
      </span>
      <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em' }}>{name}</span>
    </div>
  );

  if (!slot) return null;

  if (isWeeklyBrandError(slot)) {
    const kind = slot.error ? slot.error.kind : '';
    const message = slot.error ? slot.error.message : '';
    return (
      <div style={{ marginBottom: '26px' }}>
        {head}
        <div
          style={{
            border: '1px dashed var(--border2)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
            color: 'var(--dim)',
          }}
        >
          <Icon name="report" size={30} color="#e0a23c" style={{ marginBottom: '10px' }} />
          <div style={{ fontSize: '14px', color: 'var(--text)', fontWeight: 600, marginBottom: '5px' }}>Rapor hesaplanamadı</div>
          <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
            {kind === 'unconfigured'
              ? 'BackOffice erişim anahtarları sunucuda tanımlı değil.'
              : 'Bu marka için haftalık rapor üretilemedi.'}
          </div>
          {message ? <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginTop: '8px' }}>{message}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '30px' }}>
      {head}
      <Caveats report={slot} />
      <MoneyStrip report={slot} brand={brand} />
      <ListsTable report={slot} brand={brand} week={week} lang={lang} />
      <Donuts report={slot} brand={brand} />
      <AgentTables report={slot} brand={brand} week={week} />
    </div>
  );
}

// ── Uyarı şeridi ─────────────────────────────────────────────────────────────

/**
 * Sunucunun `caveats` satırları — rakamlara neden güvenilmeyeceğini söyleyen TEK yer.
 *
 * Neden var (2026-09-23): paylaşılan WhatsApp dosyasının 403'ü her yeniden taramayı
 * düşürüyordu; sunucu son başarılı taramayı sunmaya devam etti ve panel günlerce hafta
 * ortasının donmuş sayılarını gösterdi (Vegas 14.09: 7309 çağrı / FTD 225, gerçeği
 * 10027 / 398) — ekranda bunu söyleyen hiçbir şey yoktu. Bir sayı yanlış olabiliyorsa
 * bunu ekranın kendisi söylemeli.
 *
 * Metin SUNUCUDAN gelir (tarih + süre taşır → sözlüğe giremez, §2); sabit başlık
 * çevrilir. Alan yoksa hiçbir şey çizilmez.
 */
function Caveats({ report }: { report: WeeklyBrandReport }) {
  const lines = report.caveats || [];
  if (!lines.length) return null;
  return (
    <div
      style={{
        border: '1px solid var(--warn-border)',
        background: 'rgba(224,162,60,.09)',
        borderRadius: '12px',
        padding: '11px 14px',
        marginBottom: '14px',
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
      }}
    >
      <Icon name="report" size={16} color="#e0a23c" style={{ flex: 'none', marginTop: '1px' }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#e0a23c', marginBottom: '3px' }}>Veri uyarısı</div>
        {lines.map((c) => (
          <div key={c} style={{ fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text)' }}>{c}</div>
        ))}
      </div>
    </div>
  );
}

// ── Para şeridi ──────────────────────────────────────────────────────────────

function MoneyStrip({ report, brand }: { report: WeeklyBrandReport; brand: BrandId }) {
  const s = report.summary;
  const team = report.teamAverage;
  const msg = report.messaging;
  const showMessaging = MESSAGING_BRANDS.includes(brand);
  const sms = report.smsSummary;
  // Otomatik göndericisi olmayan markada (Hera) Toplam SMS = Temsilci SMS, AYNI sayı → tek kart yeter; mesaj kartı da
  // yoksa o kart 1. satırdaki boş yuvaya oturur ve şerit iki TAM 4'lü satır olur (Asya: "4 boxes, no void").
  const agentSmsOnly = Boolean(sms && !sms.auto && sms.autoReason === 'none');
  const smsInFirstRow = agentSmsOnly && !showMessaging;
  // "Toplam Çağrı" = GERÇEK arama (Arama Yapılmadı HARİÇ) — panelde HER YERDE no_call sayılmaz
  // (owner 2026-08-19). Liste tablosunun Toplam satırıyla AYNI taban/oran (callsExclNoCallOf /
  // answerRateExclNoCall, sonuç kırılımı üzerinden) → iki sayı BİREBİR uyuşur. Ham summary.calls /
  // summary.answerRatePct (no_call + bot dahil) artık BURADA gösterilmez.
  // "Yanıtlayan Sayısı" = `ANSWERED_KEYS` toplamı (payda değil, pay) — görüşme sayısından GENİŞ
  // (`Meşgul / Tekrar Aranacak` + `Markayı Duyunca Kapattı` burada var, görüşmede yok).
  const totals = useMemo(() => resultTotals(report), [report]);
  const answered = useMemo(() => answeredOf(totals), [totals]);
  const calls = useMemo(() => callsExclNoCallOf(totals), [totals]);
  const answerRate = useMemo(() => answerRateExclNoCall(totals), [totals]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: '12px', marginBottom: '18px' }}>
      {/* 4×2 düzen (owner): 1. satır çağrı/mesaj/yatırım, 2. satır katkı + ekip başarı. Hera'da mesaj kartı
          yok (#255/#256) → onun yuvasında Temsilci SMS (yukarıda); SMS özeti gelmezse 1. satır 3 kart kalır ve
          `newRow` 2. satırı yine 1. sütundan başlatır. */}
      <StatTile title="Toplam Çağrı / Yanıtlayan Sayısı" value={fmtMoney(calls) + ' / ' + fmtMoney(answered)}>
        <LabelNum label="Cevaplama:" value={fmtPct1(answerRate)} />
      </StatTile>
      {showMessaging ? (
        <StatTile title="WhatsApp - Telegram" value={msg ? fmtMoney(msg.total) + ' mesaj' : ''}>
          <LabelNum label="Cevaplama:" value={fmtPct1(msg ? msg.answerRatePct : null)} />
        </StatTile>
      ) : smsInFirstRow && sms ? (
        <AgentSmsTile sms={sms} />
      ) : null}
      <StatTile title="Yatırım (24s)" value={fmtMoney(s.deposit12h)} />
      <StatTile title="Yatırım (48s)" value={fmtMoney(s.deposit36h)} />
      <StatTile
        newRow
        title="Site katkısı (24s)"
        value={fmtPct1(s.contribution12hPct)}
        note={s.contribution12hPct == null ? 'BackOffice verisi yok' : undefined}
      />
      <StatTile
        title="Site katkısı (48s)"
        value={fmtPct1(s.contribution36hPct)}
        note={s.contribution36hPct == null ? 'BackOffice verisi yok' : undefined}
      />
      {/* Ekip başarı ort. — 24s ve 48s AYRI kartlar (owner: sekme tıklamak istemiyor). */}
      <StatTile title="Ekip başarı ort. (24s)" value={fmtPct1(team?.successRate12hPct)} />
      <StatTile title="Ekip başarı ort. (48s)" value={fmtPct1(team?.successRate36hPct)} />
      {/* 3. satır: SMS sayaçları (görev #189) — eski sunucu alanı göndermezse satır hiç çizilmez; Hera'da 1. satırda. */}
      {sms && !smsInFirstRow ? <SmsTiles sms={sms} /> : null}
    </div>
  );
}

/** Temsilci SMS kartı — eklentinin gönderdiği (sms_events), başarısızlar alt satırda. SMS satırında ve (Hera) 1. satırda. */
function AgentSmsTile({ sms }: { sms: WeeklySmsSummary }) {
  return (
    <StatTile
      title="Temsilci SMS"
      value={sms.agent ? fmtMoney(sms.agent.sent) : ''}
      note={sms.agent ? undefined : 'SMS kayıtları okunamadı'}
    >
      <LabelNum label="Başarısız:" value={sms.agent && sms.agent.failed ? fmtMoney(sms.agent.failed) : ''} />
    </StatTile>
  );
}

/** "DD.MM'den beri" — otomatik SMS verisi hafta ortasında başladıysa (tarih taşır → translatePhrase regex'i). */
function sinceNote(day: string | null): string | undefined {
  return day ? day.slice(8, 10) + '.' + day.slice(5, 7) + "'den beri" : undefined;
}

/**
 * Toplam SMS · Temsilci SMS · Otomatik SMS (görev #189, asya 2026-10-08). Temsilci = eklentinin gönderdiği (sms_events);
 * Otomatik = kayit-oto / prime-kayit-ftd'nin kayıt + FTD SMS'i (migration 032). Ölçülmeyen değer BOŞ + neden notu;
 * hafta ortasında başlayan otomatik veri "…'den beri" der — kısmi bir sayı haftanın toplamı gibi okunmasın.
 * Otomatik göndericisi OLMAYAN markada (sunucu `autoReason:'none'` — Hera, `SMS_AUTO_BRANDS` dışı) kart hiç
 * çizilmez (görev #255/#256, Asya: "remove … Automatic SMS in hera"); eskiden "Bu markada otomatik SMS yok" yazan
 * boş kart duruyordu. Toplam yine tam (otomatik yok = Toplam = Temsilci). Gönderici var ama veri yok/okunamadı →
 * kart KALIR, nedeni söyler. Mesaj kartı da olmayan markada bu satır hiç çizilmez: MoneyStrip tek Temsilci SMS
 * kartını 1. satıra koyar (Toplam aynı sayı olurdu).
 */
function SmsTiles({ sms }: { sms: WeeklySmsSummary }) {
  const noAutoSender = !sms.auto && sms.autoReason === 'none';
  const autoNote = sms.auto
    ? sinceNote(sms.auto.since)
    : sms.autoReason === 'not_yet'
      ? 'Henüz veri gelmedi'
      : 'Otomatik SMS okunamadı';
  const totalNote = !sms.available
    ? 'SMS kayıtları okunamadı'
    : sms.totalPartial
      ? sms.auto
        ? 'Otomatik SMS ' + sinceNote(sms.auto.since)
        : 'Otomatik SMS dahil değil'
      : undefined;
  return (
    <>
      <StatTile title="Toplam SMS" value={sms.total == null ? '' : fmtMoney(sms.total)} note={totalNote} />
      <AgentSmsTile sms={sms} />
      {noAutoSender ? null : (
        <StatTile title="Otomatik SMS" value={sms.auto ? fmtMoney(sms.auto.sent) : ''} note={autoNote}>
          {sms.auto ? (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {/* 'Kayıt:' sözlükte başka bir anlamda ("Records:") — ayrı anahtar. */}
              <LabelNum label="Kayıt SMS:" value={fmtMoney(sms.auto.reg)} />
              <LabelNum label="FTD SMS:" value={fmtMoney(sms.auto.ftd)} />
            </div>
          ) : null}
        </StatTile>
      )}
    </>
  );
}

/** "SMS başlıkları" halkası: temsilci SMS'i şablon başlığına göre + otomatik kayıt / FTD SMS'i. Sunucu alanı yoksa null
 *  (kart "henüz gelmiyor" der); okunamadıysa da null — 0'lık bir halka "hiç SMS yok" derdi. */
function extractSmsTitles(b: WeeklyBrandReport): Record<string, number> | null {
  const s = b.smsSummary;
  if (!s || !s.agent) return null;
  const m: Record<string, number> = {};
  Object.entries(s.agent.byTemplate).forEach(([id, n]) => {
    const label = smsTemplateLabel(id);
    m[label] = (m[label] || 0) + n;
  });
  if (s.auto) {
    if (s.auto.reg) m['Otomatik · Kayıt'] = s.auto.reg;
    if (s.auto.ftd) m['Otomatik · FTD'] = s.auto.ftd;
  }
  return m;
}

// ── Liste tablosu ────────────────────────────────────────────────────────────

function ListsTable({ report, brand, week, lang }: { report: WeeklyBrandReport; brand: BrandId; week: string; lang: Lang }) {
  const rows = useMemo(() => tableRows(report), [report]);
  const s = report.summary;

  // Toplam satırının SONUÇ hücreleri satırlardan toplanır: özet yalnız çağrı/görüşme
  // taşır. Kovalar birleşik satırın YERİNE geçtiği için toplama çift saymaz.
  const totals = useMemo(() => {
    const acc: Record<string, number> = {};
    rows.forEach((r) => RESULTS.forEach((st) => (acc[st.label] = (acc[st.label] || 0) + ((r.results || {})[st.label] || 0))));
    return acc;
  }, [rows]);

  // pic 4 (sahibin kararı): "Arama Yapılmadı" satırları GERÇEK arama değildir → ne Toplam
  // satırının TOPLAM'ına, ne cevaplama oranına, ne DE her liste satırının TOPLAM sütununa
  // girer (sahibin elle tuttuğu "VS" tablosu böyle okunur). Toplam satırı + her liste satırı
  // aynı tabana oturduğu için satır TOPLAM'ları Toplam satırına birebir toplanır.
  //
  // Taban = GÖSTERİLEN sonuç sütunlarının toplamı − no_call, s.calls DEĞİL: motorun `total`'ı
  // tabloda sütunu OLMAYAN statüleri de sayar (not_found, boş-canon, unknownResults) →
  // s.calls > Σ sütun. Görünen sütunlardan düşülünce satır KENDİ içinde tutar (callsExclNoCallOf).
  const callsExclNoCall = useMemo(() => callsExclNoCallOf(totals), [totals]);
  const answerRateExcl = useMemo(() => answerRateExclNoCall(totals), [totals]);
  const success12 = useMemo(() => pooledListSuccessPct(rows, s.conversations, true), [rows, s.conversations]);
  const success36 = useMemo(() => pooledListSuccessPct(rows, s.conversations, false), [rows, s.conversations]);

  const onCsv = () => {
    const head = [
      'Liste', 'Toplam', ...RESULTS.map((st) => st.label),
      'Cevaplama %', 'Basari 24S %', 'Basari 48S %', 'Yatiran 24S', 'Yatiran 48S',
      'Yatirim Payi 12S %', 'Yatirim Payi 36S %', 'Gorusulen Uye',
    ];
    downloadCSV(
      'haftalik-rapor-listeler-' + brand + '-' + week + '.csv',
      [head as (string | number)[]].concat(
        rows.map((r) => [
          r.label,
          callsExclNoCallOf(r.results || {}),
          ...RESULTS.map((st) => (r.results || {})[st.label] || 0),
          // Aramasız liste → BOŞ hücre, 0 DEĞİL (ekrandaki boşluğun CSV karşılığı; §6).
          answerRateExclNoCall(r.results || {}) ?? '',
          r.successRate12hPct ?? '',
          r.successRate36hPct ?? '',
          r.deposited12h ?? '',
          r.deposited36h ?? '',
          r.depositShare12hPct == null ? '' : r.depositShare12hPct,
          r.depositShare36hPct == null ? '' : r.depositShare36hPct,
          r.conversations,
        ]),
      ),
    );
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const [pngBusy, onPng] = usePngExport(cardRef, 'haftalik-rapor-listeler-' + brand + '-' + week + '.png');

  const cell: React.CSSProperties = { ...num, fontSize: '12.5px', color: 'var(--text)' };

  return (
    <div ref={cardRef} style={{ ...cardStyle, marginBottom: '18px', overflow: 'hidden' }}>
      <PngCaption brand={brand} week={week} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 18px', borderBottom: '1px solid var(--border)' }}>
        <SectionTitle n="1" title="Liste Kırılımı" sub="Sekme × sonuç dağılımı · Arama Talepleri kaynağına göre bölünür" />
        <CsvButton onClick={onCsv} />
        <PngButton onClick={onPng} busy={pngBusy} />
      </div>
      {/* data-png-unclip: PNG dışa aktarma bu kaydırıcıyı geçici olarak açar → kırpılan sütunlar da basılır. */}
      <div data-png-unclip style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: TABLE_MIN_WIDTH }}>
          <div
            lang={lang}
            style={{
              display: 'grid',
              gridTemplateColumns: TABLE_COLS,
              gap: '8px',
              padding: '9px 18px',
              borderBottom: '1px solid var(--border)',
              ...headCellStyle,
            }}
          >
            <span>LİSTE</span>
            <span style={{ textAlign: 'right' }}>TOPLAM</span>
            {/* Etiketler getStatusSystems().tr'den gelir (elle yazılmaz); büyük harf
                CSS ile — böylece DOM'daki metin sözlükteki TR anahtarın AYNISI kalır
                ve EN kaplaması "ANSWERED" yazabilir. */}
            {RESULTS.map((st) => (
              <span key={st.key} style={{ textAlign: 'right' }}>
                {st.label}
              </span>
            ))}
            <span style={{ textAlign: 'right' }}>CEVAPLAMA</span>
            <span style={{ textAlign: 'right' }}>BAŞARI 24S</span>
            <span style={{ textAlign: 'right' }}>BAŞARI 48S</span>
            <span style={{ textAlign: 'right' }}>YATIRIM PAYI 24S</span>
            <span style={{ textAlign: 'right' }}>YATIRIM PAYI 48S</span>
            <span style={{ textAlign: 'right' }}>GÖRÜŞÜLEN ÜYE</span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: TABLE_COLS,
              gap: '8px',
              padding: '11px 18px',
              borderBottom: '1px solid var(--border2)',
              alignItems: 'center',
              background: 'var(--surface2)',
              fontWeight: 700,
            }}
          >
            <span style={{ fontSize: '12.5px', color: 'var(--text)' }}>Toplam</span>
            <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(callsExclNoCall)}</span>
            {RESULTS.map((st) => (
              <span key={st.key} style={{ ...cell, fontWeight: 700 }}>
                {fmtMoney(totals[st.label] || 0)}
              </span>
            ))}
            <span style={{ ...cell, fontWeight: 700 }}>{fmtPct1(answerRateExcl)}</span>
            <span style={{ ...cell, fontWeight: 700 }}>{fmtPct1(success12)}</span>
            <span style={{ ...cell, fontWeight: 700 }}>{fmtPct1(success36)}</span>
            {/* Toplamın "payı" %100 olurdu (anlamsız). Onun yerine, satır %'lerinin PAYDASI
                olan toplam yatırım TUTARINI göster (sahibin isteği 2026-08-16): liste satırları
                bu sütunda % taşır, TOPLAM satırı TL — birim farkı kasıtlı, bu sütunun tabanı
                budur. deposit12h/36h = totals.raw (MoneyStrip "Yatırım 12s/36s" ile AYNI sayı,
                payların hesaplandığı payda). */}
            <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(s.deposit12h)}</span>
            <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(s.deposit36h)}</span>
            <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(s.conversations)}</span>
          </div>

          {rows.map((r) => (
            <div
              key={r.key}
              style={{
                display: 'grid',
                gridTemplateColumns: TABLE_COLS,
                gap: '8px',
                padding: '10px 18px',
                borderBottom: '1px solid var(--border)',
                alignItems: 'center',
              }}
            >
              <div style={{ minWidth: 0 }}>
                {/* Sheet değeri — EN kaplamasında da olduğu gibi basılır. */}
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.label}
                </div>
                {r.subLabel ? <div style={{ fontSize: '10px', color: 'var(--faint)' }}>{r.subLabel}</div> : null}
              </div>
              <span style={cell}>{fmtMoney(callsExclNoCallOf(r.results || {}))}</span>
              {RESULTS.map((st) => (
                <span key={st.key} style={{ ...cell, color: (r.results || {})[st.label] ? 'var(--text)' : 'var(--faint)' }}>
                  {fmtMoney((r.results || {})[st.label] || 0)}
                </span>
              ))}
              <span style={{ ...cell, fontWeight: 600 }}>{fmtPct1(answerRateExclNoCall(r.results || {}))}</span>
              {/* Sayı title'da ("12 / 48 görüşme") — çıplak % paydayı sorgulatır. Görüşmesiz / DAVET → BOŞ. */}
              <span style={{ ...cell, fontWeight: 600 }} title={successTitle(r.deposited12h, r.conversations)}>{fmtPct1(r.successRate12hPct)}</span>
              <span style={{ ...cell, fontWeight: 600 }} title={successTitle(r.deposited36h, r.conversations)}>{fmtPct1(r.successRate36hPct)}</span>
              {/* null ≠ 0: para sütunu OLMAYAN sekme (DAVET) burada BOŞ kalır. */}
              <span style={cell}>{fmtPct1(r.depositShare12hPct)}</span>
              <span style={cell}>{fmtPct1(r.depositShare36hPct)}</span>
              <span style={cell}>{fmtMoney(r.conversations)}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Toplam satırı + her liste satırının TOPLAM'ı sahibin "VS" tablosuyla aynı hizada:
          "Arama Yapılmadı" (gerçek arama değil) ne TOPLAM'a ne de cevaplama oranına girer.
          Sonuç sütunları ise ham sayıyı gösterir — TOPLAM = Σ sonuç sütunu − Arama Yapılmadı. */}
      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', fontSize: '11.5px', color: 'var(--faint)' }}>
        TOPLAM ve cevaplama oranı "Arama Yapılmadı" satırlarını (gerçek arama değil) saymaz; sonuç sütunları ham sayıyı gösterir. Hiç arama çıkmamış listenin cevaplama oranı BOŞ kalır; Toplam oranı havuzlanmıştır (satır ortalaması değil), o yüzden boş listeler onu aşağı çekmez. BAŞARI = yatırım yapan üye ÷ görüşülen üye (Temsilci Karnesi'ndeki tanım); Toplam satırında havuzlanmıştır.
      </div>
    </div>
  );
}

// ── Dağılımlar ───────────────────────────────────────────────────────────────

function Donuts({ report, brand }: { report: WeeklyBrandReport; brand: BrandId }) {
  // "Çağrı payı" = gerçek arama payı: Liste Kırılımı'nın TOPLAM sütunuyla AYNI taban
  // (gösterilen sonuçlar − Arama Yapılmadı), yoksa aynı liste iki bölümde farklı sayı gösterir.
  const listMap = useMemo(() => {
    const m: Record<string, number> = {};
    tableRows(report).forEach((r) => (m[r.label] = (m[r.label] || 0) + callsExclNoCallOf(r.results || {})));
    return m;
  }, [report]);
  const msgMap = useMemo(() => extractMessagingReasons(report), [report]);
  const bonusMap = useMemo(() => extractBonusLists(report), [report]);
  const slackMap = useMemo(() => extractCallRequestReasons(report, 'SLACK'), [report]);
  const formMap = useMemo(() => extractCallRequestReasons(report, 'FORM'), [report]);
  const takipMap = useMemo(() => extractCallRequestReasons(report, 'TAKİP'), [report]);
  const smsMap = useMemo(() => extractSmsTitles(report), [report]);

  // Sıra: iki "çağrı payı" halkası (tüm listeler → BONUS'un kampanya kırılımı), sonra üç
  // talep-sebebi halkası (SLACK / FORM / TAKİP sekmeleri), en sonda mesaj sekmesi — 2 sütun × 3.
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 2px 14px' }}>
        <SectionTitle n="2" title="Dağılımlar" sub="Çağrı ve talep sebebi kırılımları" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: '14px', marginBottom: '20px' }}>
        <DonutCard title="Tüm listeler" sub="Çağrı payı" map={listMap} />
        <DonutCard title="Bonus listeleri" sub="Çağrı payı" map={bonusMap} />
        <DonutCard title="Slack sebepleri" sub="Talep sebebi" map={slackMap} />
        <DonutCard title="Form sebepleri" sub="Talep sebebi" map={formMap} />
        <DonutCard title="Takip sebepleri" sub="Talep sebebi" map={takipMap} />
        {/* Hera'da mesaj halkası yok (#255/#256, MoneyStrip kartıyla birlikte) → 6 halka, 2 × 3. */}
        {MESSAGING_BRANDS.includes(brand) ? <DonutCard title="WhatsApp / Telegram" sub="Mesaj sebebi" map={msgMap} /> : null}
        {/* Görev #189 yorumu (asya): "add sms titles in Distributions". Eski sunucuda alan yok → kart hiç çizilmez. */}
        {report.smsSummary ? <DonutCard title="SMS başlıkları" sub="Gönderilen SMS · şablon" map={smsMap} /> : null}
      </div>
    </>
  );
}

// ── Temsilci karnesi ─────────────────────────────────────────────────────────

function AgentTables({ report, brand, week }: { report: WeeklyBrandReport; brand: BrandId; week: string }) {
  const scored = SCORED_BRANDS.includes(brand);
  const qms = QMS_BRANDS.includes(brand);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 2px 14px' }}>
        <SectionTitle n="3" title="Temsilci Karnesi" sub="Görüşme başına yatırım dönüşümü" />
      </div>
      {/* 12s/36s tabloları: yer varsa yan yana, yoksa alt alta. auto-fit + minmax
          eşik altında (≈600px'in altı) TEK sütuna düşer → dar ekranda eski tam-genişlik
          davranışı korunur, para sütunu kırpılmaz. SKOR sütunu (+70px) eşiği 680px'e taşıdı; KONUŞMA SÜRESİ (+70px +8 gap) +78px.
          QMS'siz Hera tablosunun en dar hali ~740px (ölçüldü) → 742px eşiği kırpmadan sığar, değişmedi. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(' + (scored ? '822px' : '742px') + ', 1fr))', gap: '14px', marginBottom: '20px' }}>
        <AgentTable report={report} brand={brand} week={week} window12 scored={scored} qms={qms} />
        {/* 2026-10-22 (sahip): 48s karnesi de skorlu — #153'ün "48s SKOR'suz" kararı geri alındı; Haftanın Temsilcisi kartı 24s kalır. */}
        <AgentTable report={report} brand={brand} week={week} window12={false} scored={scored} qms={qms} />
      </div>
      {/* Haftanın Temsilcisi (görev #143/#144; #153: yalnız 24s). auto-fill: tek kart geniş ekranda yarım
          genişlikte kalır (auto-fit tüm satıra yayardı), dar ekranda tam genişlik. Hera'da YOK. */}
      {scored && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(680px, 1fr))', gap: '14px', marginBottom: '20px' }}>
          <AgentRankTable report={report} brand={brand} week={week} window12 />
        </div>
      )}
    </>
  );
}

/** /10 with a Turkish decimal comma, or an em dash. NOT a percentage — the QMS panel
 *  scores out of 10 and rendering it as '63%' beside real percentages would read as one. */
function fmtScore(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(1).replace('.', ',');
}

/** Colour follows the panel's own bands: >=9 good, >=7 warn, below that bad. */
export function scoreColor(v: number | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (v >= 9) return 'var(--good)';
  if (v >= 7) return 'var(--warn)';
  return 'var(--bad)';
}

/** SKOR (Haftanın Temsilcisi, 10 üzerinden) kendi bantları: >=8 yeşil (sahip 2026-10-22), >=7 amber, altı kırmızı.
 *  QMS hücreleri panelin 9'luk bandında kalır — `scoreColor`. */
function finalScoreColor(v: number): string {
  if (v >= 8) return 'var(--good)';
  if (v >= 7) return 'var(--warn)';
  return 'var(--bad)';
}

/**
 * An agent with no evaluation this week shows an em dash, never 0,0 — "not evaluated"
 * and "scored zero" are opposite facts and 0,0 is the score of a CRITICAL call here.
 * The evaluation count rides in the title so a 10,0 built on one call is not mistaken
 * for a settled average.
 */
function QmsScoreCell({ qms }: { qms?: WeeklyAgentQms | null }) {
  const style: React.CSSProperties = { ...num, fontSize: '12.5px', fontWeight: 700, color: scoreColor(qms?.avgScore) ?? 'var(--faint)' };
  const title = qms ? qms.evalCount + ' değerlendirme' : 'Bu hafta değerlendirme yok';
  return <span style={style} title={title}>{fmtScore(qms?.avgScore)}</span>;
}

function QmsCriticalCell({ qms }: { qms?: WeeklyAgentQms | null }) {
  const n = qms?.criticalCount ?? null;
  const style: React.CSSProperties = { ...num, fontSize: '12.5px', fontWeight: 700, color: n ? 'var(--bad)' : 'var(--faint)' };
  return <span style={style}>{n === null ? '—' : fmtMoney(n)}</span>;
}

/**
 * Haftada gönderilen SMS (görev #151/#152). Başarısızlar title'da; SMS okunamadıysa (`null`)
 * hücre BOŞ kalır (§6) — 0 ise "hiç atmadı", gerçek bir cevap.
 */
function SmsCell({ sms, style }: { sms?: WeeklyAgentSms | null; style: React.CSSProperties }) {
  if (!sms) return <span style={style} />;
  const title = sms.failed ? sms.sent + ' gönderildi · ' + sms.failed + ' başarısız' : undefined;
  return <span style={sms.sent ? style : { ...style, color: 'var(--faint)' }} title={title}>{fmtMoney(sms.sent)}</span>;
}

/**
 * Haftalık konuşma süresi (softphone). `null` = bu hafta oturumu yok / okunamadı → BOŞ hücre
 * (§6) — 0 basılırsa "hiç aramadı" okunurdu. Arama/bağlanan/ortalama title'da.
 */
function TalkCell({ talk, style }: { talk?: WeeklyAgentTalk | null; style: React.CSSProperties }) {
  if (!talk) return <span style={style} />;
  const title = 'Arama ' + talk.dials + ' · bağlanan ' + talk.connected
    + (talk.avgTalkSec != null ? ' · ortalama ' + fmtHMS(talk.avgTalkSec) : '');
  return <span style={talk.talkSec ? style : { ...style, color: 'var(--faint)' }} title={title}>{fmtHMS(talk.talkSec)}</span>;
}

function AgentTable({
  report,
  brand,
  week,
  window12,
  scored,
  qms,
}: {
  report: WeeklyBrandReport;
  brand: BrandId;
  week: string;
  window12: boolean;
  scored: boolean;
  qms: boolean;
}) {
  const cols = AGENT_COLS + (qms ? QMS_COLS : '') + (scored ? ' 62px' : '');
  // Sıra = Haftanın Temsilcisi skoru (agent-ranking.ts, aynı pencere) — beraberlikte yatırım tutarı.
  // Skor dışı temsilci (SCORE_EXCLUDED_AGENTS, görev #274/#275) haritada yok → SKOR boş, hep en altta.
  const ranked = useMemo(
    () => new Map(scored ? rankAgents(report.agents, window12, brand).map((r) => [r.name, r]) : []),
    [report, window12, scored, brand],
  );
  const rows = useMemo(() => {
    const list = Array.isArray(report.agents) ? report.agents.slice() : [];
    const sc = (a: (typeof list)[number]) => ranked.get(a.name)?.score10 ?? -1;
    return list.sort((a, b) => sc(b) - sc(a) || (window12 ? b.deposit12h - a.deposit12h : b.deposit36h - a.deposit36h));
  }, [report, window12, ranked]);
  const team = report.teamAverage;

  const dep = (r: (typeof rows)[number]) => (window12 ? r.deposit12h : r.deposit36h);
  const cnt = (r: (typeof rows)[number]) => (window12 ? r.deposited12h : r.deposited36h);
  const rate = (r: (typeof rows)[number]) => (window12 ? r.successRate12hPct : r.successRate36hPct);

  // pic 6: alt satır sütun TOPLAMLARINI taşır (görüşme, yatıran, yatırım tutarı); başarı
  // sütunu ise sahibin istediği ORTALAMA (motorun ağırlıksız ekip ortalaması) — bu yüzden
  // Yatıran/Görüşme oranı Başarı'ya birebir bölünmeyebilir.
  const totalCalls = rows.reduce((n, r) => n + (r.calls || 0), 0);
  const totalConversations = rows.reduce((n, r) => n + (r.conversations || 0), 0);
  const totalDeposited = rows.reduce((n, r) => n + (cnt(r) || 0), 0);
  const totalDeposit = rows.reduce((n, r) => n + (dep(r) || 0), 0);
  // A sum, not an average — every agent's critical errors add up to the brand's.
  const totalCriticals = rows.reduce((n, r) => n + (r.qms?.criticalCount || 0), 0);
  const teamQms = report.qms;
  // SMS: sütun TOPLAMI (karnedeki satırlar). Karnede satırı olmayan temsilcinin SMS'i title'da adlandırılır.
  const teamSms = report.sms;
  const totalSms = rows.reduce((n, r) => n + (r.sms?.sent || 0), 0);
  const unmatchedSms = teamSms && teamSms.available
    ? teamSms.unmatched.filter((u) => u.sent > 0).map((u) => u.name + ' ' + u.sent).join(', ')
    : '';
  // Konuşma: yalnız ÖLÇÜLEN satırların sütun TOPLAMI; marka o hafta hiç ölçülmediyse hücre BOŞ.
  const teamTalk = report.talk;
  const talkMeasured = !!teamTalk && teamTalk.available && teamTalk.measured;
  const totalTalkSec = rows.reduce((n, r) => n + (r.talk?.talkSec || 0), 0);
  const unmatchedTalk = teamTalk && teamTalk.available && teamTalk.measured
    ? teamTalk.unmatched.filter((u) => u.talkSec > 0).map((u) => u.name + ' ' + fmtHMS(u.talkSec)).join(', ')
    : '';

  const onCsv = () => {
    const head = ['Temsilci', 'Toplam Cagri', 'SMS', 'SMS Basarisiz', 'Konusma (sn)', 'Arama (softphone)', 'Baglanan',
      'Gorusme', 'Yatiran', 'Basari %', 'Yatirim']
      .concat(qms ? ['QMS Kalite', 'QMS Degerlendirme', 'Kritik Hata'] : [])
      .concat(scored ? ['Skor (10)'] : []);
    downloadCSV(
      'haftalik-rapor-temsilci-' + (window12 ? '12s' : '36s') + '-' + brand + '-' + week + '.csv',
      [head as (string | number)[]].concat(rows.map((r) => [
        r.name, r.calls, r.sms?.sent ?? '', r.sms?.failed ?? '',
        // Ölçülmeyen satır BOŞ (''), 0 değil — §6.
        r.talk?.talkSec ?? '', r.talk?.dials ?? '', r.talk?.connected ?? '',
        r.conversations, cnt(r), agentSuccessBase(r) > 0 ? rate(r) : '', dep(r),
      ]
        // '' rather than 0 for an unevaluated agent: a spreadsheet AVERAGE() must not
        // count them, and a 0 here is indistinguishable from a genuine critical-call 0.
        .concat(qms ? [r.qms?.avgScore ?? '', r.qms?.evalCount ?? '', r.qms?.criticalCount ?? ''] : [])
        .concat(scored ? [ranked.get(r.name)?.score10 ?? ''] : []))),
    );
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const [pngBusy, onPng] = usePngExport(cardRef, 'haftalik-rapor-temsilci-' + (window12 ? '12s' : '36s') + '-' + brand + '-' + week + '.png');

  const cell: React.CSSProperties = { ...num, fontSize: '12.5px', color: 'var(--text)' };

  return (
    <div ref={cardRef} style={{ ...cardStyle, overflow: 'hidden' }}>
      <PngCaption brand={brand} week={week} />
      <div style={cardHeadStyle}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{window12 ? '24 saatlik tablo' : '48 saatlik tablo'}</div>
          <div style={{ fontSize: '11px', color: 'var(--faint)' }}>{scored ? 'Skora göre sıralı · QMS %50 · Başarı oranı %40 · Yatırım %10 · Kritik hata −0,1' : 'Yatırıma göre sıralı'}</div>
        </div>
        <CsvButton onClick={onCsv} small />
        <PngButton onClick={onPng} small busy={pngBusy} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: '8px',
          padding: '9px 16px',
          borderBottom: '1px solid var(--border)',
          ...headCellStyle,
        }}
      >
        <span>TEMSİLCİ</span>
        <span style={{ textAlign: 'right' }}>TOPLAM ÇAĞRI</span>
        <span style={{ textAlign: 'right' }}>SMS</span>
        <span style={{ textAlign: 'right' }} title="Softphone: bağlanan çağrıların toplam konuşma süresi">KONUŞMA SÜRESİ</span>
        <span style={{ textAlign: 'right' }}>YANITLAYAN ÜYE</span>
        <span style={{ textAlign: 'right' }}>YATIRIM YAPAN ÜYE</span>
        <span style={{ textAlign: 'right' }}>BAŞARI ORANI</span>
        <span style={{ textAlign: 'right' }}>YATIRIM TUTARI</span>
        {qms && <span style={{ textAlign: 'right' }}>QMS KALİTE</span>}
        {qms && <span style={{ textAlign: 'right' }}>KRİTİK HATA</span>}
        {scored && <span style={{ textAlign: 'right' }}>SKOR</span>}
      </div>
      {/* data-png-unclip: PNG dışa aktarma 380px kapağını geçici kaldırır → tüm temsilciler basılır. */}
      <div data-png-unclip style={{ maxHeight: '380px', overflowY: 'auto' }}>
        {rows.map((r) => (
          <div
            key={r.name}
            style={{
              display: 'grid',
              gridTemplateColumns: cols,
              gap: '8px',
              padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
            }}
          >
            {/* Sheet'teki temsilci adı — verbatim. */}
            <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.name}
            </span>
            <span style={cell}>{fmtMoney(r.calls)}</span>
            <SmsCell sms={r.sms} style={cell} />
            <TalkCell talk={r.talk} style={cell} />
            <span style={cell}>{fmtMoney(r.conversations)}</span>
            <span style={cell}>{fmtMoney(cnt(r))}</span>
            {/* Yalnız DAVET'te görüşen temsilcinin paydası 0 → BOŞ, %0,0 değil (§6). */}
            <span style={{ ...cell, fontWeight: 700 }} title={agentSuccessTitle(cnt(r), r)}>
              {fmtPct1(agentSuccessBase(r) > 0 ? rate(r) : null)}
            </span>
            <span style={{ ...cell, fontWeight: 600 }}>{fmtMoney(dep(r))}</span>
            {qms && <QmsScoreCell qms={r.qms} />}
            {qms && <QmsCriticalCell qms={r.qms} />}
            {scored && <ScoreCell r={ranked.get(r.name)} />}
          </div>
        ))}
      </div>
      {team && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            gap: '8px',
            padding: '10px 16px',
            borderTop: '1px solid var(--border2)',
            alignItems: 'center',
            background: 'var(--surface2)',
          }}
        >
          <span style={{ fontSize: '12.5px', color: 'var(--dim)', fontWeight: 600 }}>Ekip toplamı</span>
          {/* Çağrı/Görüşme/Yatıran/Tutar sütun TOPLAMI; Başarı ise ekip ORTALAMASI (sahibin isteği). */}
          <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(totalCalls)}</span>
          <span style={{ ...cell, fontWeight: 700 }} title={unmatchedSms ? 'Karnede olmayan: ' + unmatchedSms : undefined}>
            {teamSms && teamSms.available ? fmtMoney(totalSms) : ''}
          </span>
          <span style={{ ...cell, fontWeight: 700 }} title={unmatchedTalk ? 'Karnede olmayan: ' + unmatchedTalk : undefined}>
            {talkMeasured ? fmtHMS(totalTalkSec) : ''}
          </span>
          <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(totalConversations)}</span>
          <span style={{ ...cell, fontWeight: 700 }}>{fmtMoney(totalDeposited)}</span>
          <span style={{ ...cell, fontWeight: 700 }}>{fmtPct1(window12 ? team.successRate12hPct : team.successRate36hPct)}</span>
          <span style={{ ...cell, fontWeight: 600 }}>{fmtMoney(totalDeposit)}</span>
          {/* The brand figure comes from the SERVER (computed over the rows), not from
              averaging the column above it — that would weight an agent with one
              evaluation the same as one with forty. The critical tally IS a column sum. */}
          {qms && <span style={{ ...cell, fontWeight: 700 }}>{fmtScore(teamQms && teamQms.available ? teamQms.avgScore : null)}</span>}
          {qms && (
            <span style={{ ...cell, fontWeight: 700, color: totalCriticals > 0 ? 'var(--bad)' : undefined }}>
              {teamQms && teamQms.available ? fmtMoney(totalCriticals) : '—'}
            </span>
          )}
          {scored && <span />}
        </div>
      )}
    </div>
  );
}

/** 10 üzerinden skor (2 ondalık, TR virgülü); title = sıra + puan dökümü. Skor dışı temsilci → BOŞ hücre (§6). */
function ScoreCell({ r }: { r: RankedAgent | undefined }) {
  if (!r) return <span />;
  return (
    <span
      style={{ ...num, fontSize: '12.5px', fontWeight: 700, color: finalScoreColor(r.score10) }}
      title={r.rank + '. · QMS ' + fmtPts(r.qmsPoints) + ' + ' + fmtPts(r.successPoints) + ' + ' + fmtPts(r.depositPoints) + ' − ' + fmtPts(r.criticalPenalty) + ' = ' + fmtPts(r.score10)}
    >
      {r.score10.toFixed(2).replace('.', ',')}
    </span>
  );
}

// ── Haftanın Temsilcisi ──────────────────────────────────────────────────────

/** Puan (10 üzerinden), iki ondalık TR virgülü (4,85) — 0,10'luk ceza ve 0,20'lik yatırım adımı görünsün. */
const fmtPts = (v: number) => v.toFixed(2).replace('.', ',');

/**
 * Haftalık "en iyi temsilci" bonusunun sıralaması (görev #143/#144) — formül ve beraberlik
 * kuralı `agent-ranking.ts`'te. Satırlar yukarıdan aşağı skora göre (emoji YOK — sahip 2026-09-28); 1.
 * vurgulu. Ham değerler (QMS, başarı %, yatırım tutarı, kritik hata sayısı) hücre title'ında — tablo
 * sahibin hesap tablosunun sütunlarını birebir taşır. Skor dışı temsilciler (görev #274/#275) bu kartta YOK.
 */
function AgentRankTable({
  report,
  brand,
  week,
  window12,
}: {
  report: WeeklyBrandReport;
  brand: BrandId;
  week: string;
  window12: boolean;
}) {
  const rows = useMemo(() => rankAgents(report.agents, window12, brand), [report, window12, brand]);

  const onCsv = () => {
    const head = ['Sira', 'Temsilci', 'QMS Kalite', 'QMS Puani (5)', 'Basari %', 'Basari Puani (4)',
      'Yatirim', 'Yatirim Puani (1)', 'Kritik Hata', 'Kritik Ceza', 'Skor (10)'];
    const r2 = (v: number) => Math.round(v * 100) / 100;
    downloadCSV(
      'haftalik-rapor-siralama-' + (window12 ? '12s' : '36s') + '-' + brand + '-' + week + '.csv',
      [head as (string | number)[]].concat(rows.map((r) => [
        r.rank, r.name,
        // '' rather than 0: "not evaluated" is not a 0,0 score (it still earns 0 points).
        r.qmsScore ?? '', r2(r.qmsPoints), r.successRatePct, r2(r.successPoints),
        r.depositAmount, r2(r.depositPoints), r.criticalCount, r2(-r.criticalPenalty), r.score10,
      ])),
    );
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const [pngBusy, onPng] = usePngExport(cardRef, 'haftalik-rapor-siralama-' + (window12 ? '12s' : '36s') + '-' + brand + '-' + week + '.png');

  if (!rows.length) return null;

  const cell: React.CSSProperties = { ...num, fontSize: '12.5px', color: 'var(--text)' };

  return (
    <div ref={cardRef} style={{ ...cardStyle, overflow: 'hidden' }}>
      <PngCaption brand={brand} week={week} />
      <div style={cardHeadStyle}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
            {window12 ? 'Haftanın Temsilcisi · 24 saat' : 'Haftanın Temsilcisi · 48 saat'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--faint)' }}>QMS %50 · Başarı oranı %40 · Yatırım %10 · Kritik hata −0,1</div>
        </div>
        <CsvButton onClick={onCsv} small />
        <PngButton onClick={onPng} small busy={pngBusy} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: RANK_COLS,
          gap: '8px',
          padding: '9px 16px',
          borderBottom: '1px solid var(--border)',
          ...headCellStyle,
        }}
      >
        <span>SIRA</span>
        <span>TEMSİLCİ</span>
        <span style={{ textAlign: 'right' }}>QMS PUANI</span>
        <span style={{ textAlign: 'right' }}>BAŞARI PUANI</span>
        <span style={{ textAlign: 'right' }}>YATIRIM PUANI</span>
        <span style={{ textAlign: 'right' }}>KRİTİK CEZA</span>
        <span style={{ textAlign: 'right' }}>SKOR</span>
      </div>
      {/* data-png-unclip: PNG dışa aktarma 380px kapağını geçici kaldırır → tüm temsilciler basılır. */}
      <div data-png-unclip style={{ maxHeight: '380px', overflowY: 'auto' }}>
        {rows.map((r) => (
          <div
            key={r.name}
            style={{
              display: 'grid',
              gridTemplateColumns: RANK_COLS,
              gap: '8px',
              padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
              background: r.rank === 1 ? 'var(--accent-soft)' : undefined,
            }}
          >
            <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {r.rank + '.'}
            </span>
            <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: r.rank === 1 ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.name}
            </span>
            <span
              style={{ ...cell, color: r.qmsScore == null ? 'var(--faint)' : 'var(--text)' }}
              title={r.qmsScore == null ? 'Bu hafta değerlendirme yok' : r.qmsEvalCount + ' değerlendirme'}
            >
              {fmtPts(r.qmsPoints)}
            </span>
            <span style={cell} title={fmtPct1(r.successRatePct)}>{fmtPts(r.successPoints)}</span>
            <span style={cell} title={fmtMoney(r.depositAmount)}>{fmtPts(r.depositPoints)}</span>
            {/* 0 kritik hata gerçek bir cevap → soluk 0,00 (boş değil); ceza eksi işaretiyle. */}
            <span style={{ ...cell, color: r.criticalPenalty > 0 ? 'var(--bad)' : 'var(--faint)' }} title={r.criticalCount + ' kritik hata'}>
              {r.criticalPenalty > 0 ? '−' + fmtPts(r.criticalPenalty) : fmtPts(0)}
            </span>
            <span style={{ ...cell, fontWeight: 700, color: finalScoreColor(r.score10) }}>{r.score10.toFixed(2).replace('.', ',')}</span>
          </div>
        ))}
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: '11.5px', color: 'var(--faint)', lineHeight: 1.5 }}>
        QMS puanı = QMS ortalaması ÷ 2 (en fazla 5). Başarı puanı haftanın en iyi başarı oranına oranlanır (zirve 4). Yatırım puanı her tam 100.000 TL için 0,20 (en fazla 1,00). Her kritik hata skordan 0,10 düşer. Skor = toplam, 10 üzerinden (en az 0). Değerlendirmesi olmayan temsilci 0 QMS puanı alır. Skor dışı temsilciler listede yer almaz.
      </div>
    </div>
  );
}

